/**
 * Flow 6 — deploy, with the product's capacity limit in front of the user *before* they
 * commit.
 *
 * Three things this panel is responsible for, all of them product promises:
 *  1. **Ops cost shown before the user commits.** Operation quota is the product's real
 *     capacity limit (~7 pushes/day platform-wide on Explorer access). The user
 *     acknowledges a specific number, and that number is what the lease is taken against
 *     (04-contracts.md §4).
 *  2. **The hard gates are visible, not merely enforced.** Preflight (conversion
 *     tracking) and a passing dry run are shown as a checklist with reasons, so a refusal
 *     is never a mystery.
 *  3. **Nothing implies a live campaign.** Campaigns are created paused, always.
 */
import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useOpsBudget, useOpsEstimate, usePreflight, usePushBlueprint } from '@/api/hooks'
import type { AdAccount, OpsBudgetStatus, OpsEstimate, PreflightReport, ValidationReport } from '@/api/types'
import { ApiRequestError } from '@/api/client'
import { entityLabel, formatDateTime, formatNumber, platformLabel } from '@/lib/format'
import { pushesRemaining, REFERENCE_PUSH_OPERATIONS } from '@/lib/ops'
import { PushTracker } from '@/components/PushTracker'
import { Badge, Button, Cap, Checkbox, ErrorNotice, KeyValue, Meter, Panel, Spinner, StatTile } from '@/components/ui'

type GateStatus = 'pass' | 'fail' | 'unknown'

function Gate({
  title,
  status,
  detail,
  action,
}: {
  title: string
  status: GateStatus
  detail: string
  action?: React.ReactNode
}) {
  const tone = status === 'pass' ? 'ok' : status === 'fail' ? 'danger' : 'neutral'
  return (
    <li
      data-testid="push-gate"
      data-status={status}
      className="flex flex-wrap items-start gap-3 border-b border-ink-800 px-1 py-2.5 last:border-b-0"
    >
      <span
        aria-hidden="true"
        className={`mt-[6px] size-2.5 shrink-0 rounded-full ${
          status === 'pass' ? 'bg-signal-ok' : status === 'fail' ? 'bg-signal-danger' : 'bg-ink-500'
        }`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] text-mist-100">{title}</span>
          <Badge tone={tone}>{status === 'pass' ? 'satisfied' : status === 'fail' ? 'blocking' : 'not run'}</Badge>
        </div>
        <p className="mt-0.5 text-[12px] text-mist-400">{detail}</p>
      </div>
      {action}
    </li>
  )
}

export function PushPanel({
  blueprintId,
  version,
  accounts,
  selectedAccountId,
  onSelectAccount,
  dryRuns,
  preflights,
  onPreflight,
  onGoToDryRun,
}: {
  blueprintId: string
  version: number
  accounts: AdAccount[]
  selectedAccountId: string | null
  onSelectAccount: (accountId: string) => void
  dryRuns: Record<string, ValidationReport>
  preflights: Record<string, PreflightReport>
  onPreflight: (accountId: string, report: PreflightReport) => void
  onGoToDryRun: () => void
}) {
  const account = accounts.find((candidate) => candidate.external_id === selectedAccountId) ?? null
  const preflight = usePreflight(blueprintId)
  const estimate = useOpsEstimate(blueprintId)
  const push = usePushBlueprint(blueprintId)
  const budget = useOpsBudget()

  const [acknowledged, setAcknowledged] = useState(false)
  const [confirmPaused, setConfirmPaused] = useState(false)
  const [publishPages, setPublishPages] = useState(true)
  const [pushId, setPushId] = useState<string | null>(null)

  // Cost is always shown for the selected account's platform — never a hardcoded number.
  useEffect(() => {
    if (!account) return
    if (estimate.data?.platform === account.platform) return
    estimate.mutate(account.platform)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.platform])

  const platformBudget: OpsBudgetStatus | undefined = budget.data?.find(
    (entry) => entry.platform === account?.platform,
  )
  const dryRunReport = selectedAccountId ? (dryRuns[selectedAccountId] ?? null) : null
  const preflightReport = selectedAccountId ? (preflights[selectedAccountId] ?? null) : null
  const preflightBlockers = preflightReport?.blockers ?? []
  const preflightActions = preflightReport?.conversion_action_names ?? []
  const dryRunErrors = dryRunReport?.errors ?? []
  const hasTestAccount = account?.test_account ?? false

  const gatesReady =
    account !== null &&
    (preflightReport?.ok ?? false) &&
    (dryRunReport?.ok ?? false) &&
    acknowledged &&
    confirmPaused &&
    estimate.data !== undefined &&
    !push.isPending

  async function submit() {
    if (!account || !estimate.data) return
    const idempotencyKey = crypto.randomUUID()
    try {
      const { result, pushId: accepted } = await push.mutateAsync({
        body: {
          ad_account_id: account.external_id,
          acknowledged_operations: estimate.data.total_operations,
          confirm_paused: true,
          publish_landing_pages: publishPages,
          idempotency_key: idempotencyKey,
        },
      })
      void result
      setPushId(accepted)
    } catch {
      /* surfaced below via push.error */
    }
  }

  const leaseDenied =
    push.error instanceof ApiRequestError && push.error.code === 'OPS_LEASE_DENIED'
  const retryAfter = leaseDenied
    ? /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z)/.exec((push.error as ApiRequestError).message)?.[1] ?? null
    : null

  if (pushId) {
    return (
      <div className="flex flex-col gap-4">
        <Panel
          title="Push accepted"
          subtitle={`Version ${version} was accepted for ${platformLabel(account?.platform ?? '')} — campaigns are created paused.`}
          testId="push-accepted"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone="ok">202 Accepted</Badge>
            <span className="num text-[12px] text-mist-400">push {pushId}</span>
            <Link
              to="/pushes/$pushId"
              params={{ pushId }}
              className="text-[12px] text-forge-300 underline decoration-dotted hover:text-forge-400"
            >
              open this push on its own page
            </Link>
          </div>
        </Panel>
        <PushTracker pushId={pushId} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4" data-testid="push-panel">
      <Panel
        title="Deploy this version"
        subtitle="Landing pages publish first, then campaigns push — paused, reversed-able, and costed before you commit."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="push-account" className="label-cap">
              Ad account
            </label>
            <select
              id="push-account"
              data-testid="push-account"
              value={selectedAccountId ?? ''}
              onChange={(event) => onSelectAccount(event.target.value)}
              className="rounded-md border border-ink-600 bg-ink-850 px-2.5 py-1.5 text-sm text-mist-100"
            >
              <option value="">Select an account…</option>
              {accounts.map((candidate) => (
                <option
                  key={`${candidate.platform}:${candidate.external_id}`}
                  value={candidate.external_id}
                >
                  {platformLabel(candidate.platform)} · {candidate.name}
                  {candidate.conversion_tracking_ok ? '' : ' — no conversion tracking'}
                </option>
              ))}
            </select>
          </div>
          {account ? (
            <div className="flex flex-wrap items-center gap-2 pb-1.5">
              <Badge tone={account.status === 'enabled' ? 'ok' : 'neutral'}>{account.status}</Badge>
              <Badge tone={account.test_account ? 'info' : 'neutral'}>
                {account.test_account ? 'test account' : 'production account'}
              </Badge>
              <Badge tone={account.conversion_tracking_ok ? 'ok' : 'danger'}>
                {account.conversion_tracking_ok
                  ? `${account.conversion_action_count} conversion actions`
                  : 'no conversion tracking'}
              </Badge>
            </div>
          ) : null}
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <Panel title="Preconditions" subtitle="Hard gates. Any failure stops the push — with the reason shown, never a bare error.">
          <ul className="flex flex-col" data-testid="push-gates">
            <Gate
              title="Account can receive a campaign"
              status={
                preflightReport === null ? 'unknown' : preflightReport.ok ? 'pass' : 'fail'
              }
              detail={
                preflightReport === null
                  ? 'Preflight has not been run for this account yet.'
                  : preflightReport.ok
                    ? `Conversion tracking is configured (${preflightActions.length} actions) and ${preflightReport.conversion_actions_configured ? 'billing is in place' : 'billing is missing'}.`
                    : preflightBlockers.map((blocker) => blocker.message).join(' ')
              }
              action={
                <Button
                  size="sm"
                  data-testid="run-preflight"
                  disabled={!account || preflight.isPending}
                  onClick={async () => {
                    if (!account) return
                    const report = await preflight.mutateAsync(account.external_id)
                    onPreflight(account.external_id, report)
                  }}
                >
                  {preflight.isPending ? 'Checking…' : preflightReport ? 'Re-run' : 'Run preflight'}
                </Button>
              }
            />
            <Gate
              title="Platform dry run accepted"
              status={dryRunReport === null ? 'unknown' : dryRunReport.ok ? 'pass' : 'fail'}
              detail={
                dryRunReport === null
                  ? 'No dry run for this account yet. A passing dry run is mandatory before any live mutate.'
                  : dryRunReport.ok
                    ? `validate_only accepted ${formatNumber(dryRunReport.checked_entities)} entities.`
                    : `${dryRunErrors.length} error(s) — fix the flagged assets and re-run.`
              }
              action={
                <Button size="sm" variant="ghost" onClick={onGoToDryRun} data-testid="go-to-dry-run">
                  Open dry run
                </Button>
              }
            />
            <Gate
              title="Campaigns are created paused"
              status={confirmPaused ? 'pass' : 'unknown'}
              detail="AdForge never launches a campaign that is serving. Unpausing is a deliberate human action in the platform UI."
            />
            <Gate
              title={`Operation cost acknowledged (${formatNumber(estimate.data?.total_operations ?? 0)} ops)`}
              status={acknowledged ? 'pass' : 'unknown'}
              detail="The acknowledged number is the number the ops-budget accountant leases against."
            />
          </ul>
          {preflight.isError ? <div className="mt-3"><ErrorNotice error={preflight.error} /></div> : null}
        </Panel>

        <div className="flex flex-col gap-4">
          <Panel
            title="Operation cost of this push"
            subtitle="Operation quota is the product's real capacity limit, not a billing nicety."
            testId="ops-cost-panel"
            actions={
              estimate.isPending ? <Spinner label="Estimating…" /> : null
            }
          >
            {estimate.isError ? <ErrorNotice error={estimate.error} /> : null}
            {estimate.data ? (
              <OpsCostBody
                estimate={estimate.data}
                budget={platformBudget}
                version={version}
                hasTestAccount={hasTestAccount}
              />
            ) : (
              <p className="text-[12.5px] text-mist-400">
                Select an ad account and the estimate appears here before anything is committed.
              </p>
            )}
          </Panel>

          <Panel title="Confirm — both boxes are required" testId="push-confirm">
            <div className="flex flex-col gap-2.5">
              <Checkbox
                testId="acknowledge-ops"
                checked={acknowledged}
                disabled={!estimate.data}
                onChange={setAcknowledged}
                label={
                  <>
                    I understand this push costs{' '}
                    <span className="num font-semibold text-forge-300">
                      {formatNumber(estimate.data?.total_operations ?? 0)} operations
                    </span>{' '}
                    of the platform-wide daily quota
                    {platformBudget
                      ? ` (${formatNumber(platformBudget.available_operations)} of ${formatNumber(platformBudget.daily_ceiling)} left today)`
                      : ''}
                    .
                  </>
                }
                hint="On Explorer access the whole platform affords roughly seven pushes like this per day."
              />
              <Checkbox
                testId="confirm-paused"
                checked={confirmPaused}
                onChange={setConfirmPaused}
                label="I understand the campaigns will be created PAUSED and nothing will serve until I unpause them myself."
                hint="This is the guardrail that stops an LLM mistake becoming spend."
              />
              <Checkbox
                testId="publish-pages"
                checked={publishPages}
                onChange={setPublishPages}
                label="Publish the landing pages first"
                hint="A campaign pointing at a 404 final URL gets disapproved, so this is on by default."
              />

              {push.isError && leaseDenied ? (
                <div
                  data-testid="lease-denied"
                  className="rounded-lg border border-signal-warn/45 bg-signal-warn/10 px-3.5 py-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone="warn">queued for quota</Badge>
                    <span className="num text-[11px] text-mist-400">
                      {(push.error as ApiRequestError).code}
                    </span>
                  </div>
                  <p className="mt-1 text-[12.5px] text-mist-200">{(push.error as Error).message}</p>
                  {retryAfter ? (
                    <p className="num mt-1 text-[12px] text-signal-warn" data-testid="lease-retry-after">
                      retry after {retryAfter} ({formatDateTime(retryAfter)})
                    </p>
                  ) : null}
                  <p className="mt-1 text-[11.5px] text-mist-400">
                    Nothing was rejected on merit — the push re-enters publishing as soon as a lease is
                    granted. One tenant cannot exhaust the quota for everyone, on purpose.
                  </p>
                </div>
              ) : push.isError ? (
                <ErrorNotice error={push.error} testId="push-error" />
              ) : null}

              <div className="mt-1 flex flex-wrap items-center gap-3">
                <Button
                  variant="primary"
                  data-testid="push-blueprint"
                  disabled={!gatesReady}
                  onClick={submit}
                >
                  {push.isPending
                    ? 'Pushing…'
                    : `Push v${version} paused${estimate.data ? ` — ${formatNumber(estimate.data.total_operations)} ops` : ''}`}
                </Button>
                <span className="text-[11.5px] text-mist-500">
                  {gatesReady
                    ? 'Idempotent, reversible, and campaigns are created paused.'
                    : 'All four preconditions must be satisfied first.'}
                </span>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}

function OpsCostBody({
  estimate,
  budget,
  version,
  hasTestAccount,
}: {
  estimate: OpsEstimate
  budget: OpsBudgetStatus | undefined
  version: number
  hasTestAccount: boolean
}) {
  const entries = Object.entries(estimate.by_entity ?? {}).filter(([, count]) => count > 0)
  const max = Math.max(1, ...entries.map(([, count]) => count))
  const remaining = budget?.available_operations ?? 0
  const fits = pushesRemaining(estimate, remaining)

  return (
    <div className="flex flex-col gap-3" data-testid="ops-cost-body">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          label="Total operations"
          value={formatNumber(estimate.total_operations)}
          tone="forge"
          testId="ops-total"
        />
        <StatTile label="Requests" value={formatNumber(estimate.requests_required)} hint="10,000 ops/request cap" />
        <StatTile
          label="Remaining today"
          value={formatNumber(remaining)}
          hint={budget ? `${budget.access_level} access · ceiling ${formatNumber(budget.daily_ceiling)}` : 'budget unknown'}
        />
        <StatTile
          label="Pushes like this left"
          value={formatNumber(fits)}
          tone={fits <= 2 ? 'danger' : fits <= 6 ? 'warn' : 'ok'}
          hint="today, platform-wide"
          testId="pushes-remaining"
        />
      </div>

      {budget ? (
        <div>
          <div className="flex items-center justify-between">
            <Cap>{platformLabel(budget.platform)} quota consumed today</Cap>
            <span className="num text-[11.5px] text-mist-400">
              {formatNumber(budget.consumed_operations)} / {formatNumber(budget.daily_ceiling)}
            </span>
          </div>
          <div className="mt-1">
            <Meter
              value={budget.consumed_operations}
              max={budget.daily_ceiling}
              tone={budget.consumed_operations / budget.daily_ceiling > 0.85 ? 'danger' : 'warn'}
              label={`${budget.consumed_operations} of ${budget.daily_ceiling} operations consumed today`}
            />
          </div>
          <p className="mt-1 text-[11.5px] text-mist-500">
            utc date {budget.utc_date} · reserved {formatNumber(budget.reserved_operations)} · a reference push is{' '}
            {formatNumber(REFERENCE_PUSH_OPERATIONS)} operations
          </p>
        </div>
      ) : null}

      <div>
        <Cap>Where the operations go</Cap>
        <ul className="mt-1.5 flex flex-col gap-1.5" data-testid="ops-breakdown">
          {entries.map(([key, count]) => (
            <li key={key} className="flex items-center gap-3">
              <span className="w-[150px] shrink-0 text-[12px] text-mist-300">{entityLabel(key)}</span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-750">
                <span
                  className="block h-full rounded-full bg-forge-500"
                  style={{ width: `${(count / max) * 100}%` }}
                />
              </span>
              <span className="num w-14 shrink-0 text-right text-[12px] text-mist-200">
                {formatNumber(count)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-ink-800 pt-3">
        <KeyValue label="Pushing version">
          <span className="num">v{version}</span>
        </KeyValue>
        <KeyValue label="Target">
          <span className="num">
            {formatDateTime(new Date().toISOString())} · {hasTestAccount ? 'test account' : 'production'}
          </span>
        </KeyValue>
        <KeyValue label="Budget after">
          <span className="num">
            {formatNumber(Math.max(0, remaining - estimate.total_operations))} ops left today
          </span>
        </KeyValue>
      </div>
    </div>
  )
}
